import { Link } from 'react-router-dom';
import { useState } from 'react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import SEO from '../../components/SEO';

export default function JobsPage() {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', position: '', message: '' });
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [showBackToTop, setShowBackToTop] = useState(false);
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useState(() => {
    const handleScroll = () => setShowBackToTop(window.scrollY > 400);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const formElement = e.target as HTMLFormElement;
      const formDataToSend = new FormData(formElement);
      const response = await fetch('https://readdy.ai/api/form/d6nofg4tj1ja1m9goqm0', { method: 'POST', body: new URLSearchParams(formDataToSend as any) });
      if (response.ok) { setSubmitStatus('success'); setFormData({ name: '', email: '', phone: '', position: '', message: '' }); setTimeout(() => setSubmitStatus('idle'), 5000); }
      else { setSubmitStatus('error'); setTimeout(() => setSubmitStatus('idle'), 5000); }
    } catch { setSubmitStatus('error'); setTimeout(() => setSubmitStatus('idle'), 5000); }
  };

  const benefits = [
    { icon: 'ri-money-euro-circle-line', title: 'Faire Bezahlung', description: 'Überdurchschnittliche Vergütung und pünktliche Auszahlung' },
    { icon: 'ri-time-line', title: 'Flexible Arbeitszeiten', description: 'Work-Life-Balance durch flexible Schichtmodelle' },
    { icon: 'ri-team-line', title: 'Teamgeist', description: 'Arbeiten in einem motivierten und freundlichen Team' },
    { icon: 'ri-graduation-cap-line', title: 'Weiterbildung', description: 'Regelmäßige Schulungen und Entwicklungsmöglichkeiten' },
    { icon: 'ri-shield-check-line', title: 'Sicherer Arbeitsplatz', description: 'Langfristige Perspektiven in einem wachsenden Unternehmen' },
    { icon: 'ri-tools-line', title: 'Moderne Ausrüstung', description: 'Hochwertige Arbeitsgeräte und professionelle Ausstattung' }
  ];

  const openPositions = [
    { title: 'Reinigungskraft (m/w/d)', type: 'Vollzeit / Teilzeit', location: 'Ratingen', description: 'Für Büro- und Gebäudereinigung in verschiedenen Objekten' },
    { title: 'Hausmeister (m/w/d)', type: 'Vollzeit', location: 'Ratingen', description: 'Betreuung von Wohn- und Gewerbeobjekten, Wartung und Instandhaltung' },
    { title: 'Schlüsseldienst-Techniker (m/w/d)', type: 'Vollzeit', location: 'Ratingen & Umgebung', description: 'Notöffnungen, Schlossaustausch und Sicherheitsberatung' },
    { title: 'Teamleiter Gebäudereinigung (m/w/d)', type: 'Vollzeit', location: 'Ratingen', description: 'Koordination und Führung unseres Reinigungsteams' }
  ];

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'JobPosting',
      title: 'Reinigungskraft (m/w/d) in Ratingen',
      description: 'Wir suchen zuverlässige Reinigungskräfte für die Gebäudereinigung in Ratingen. Flexible Arbeitszeiten, faire Bezahlung und ein nettes Team erwarten Sie.',
      datePosted: '2024-01-15',
      validThrough: '2024-12-31',
      employmentType: ['FULL_TIME', 'PART_TIME'],
      hiringOrganization: {
        '@type': 'Organization',
        name: 'IGM Facility Service',
        sameAs: siteUrl,
        logo: `${siteUrl}/logo.png`,
      },
      jobLocation: {
        '@type': 'Place',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Industriestr. 37',
          addressLocality: 'Ratingen',
          postalCode: '40878',
          addressCountry: 'DE',
        },
      },
      baseSalary: {
        '@type': 'MonetaryAmount',
        currency: 'EUR',
        value: {
          '@type': 'QuantitativeValue',
          minValue: 13.50,
          maxValue: 16.00,
          unitText: 'HOUR',
        },
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'JobPosting',
      title: 'Hausmeister (m/w/d) in Ratingen',
      description: 'Erfahrener Hausmeister für vielseitige Aufgaben gesucht. Wartung, Reparaturen, Grünpflege und Winterdienst gehören zu Ihren Tätigkeiten.',
      datePosted: '2024-01-15',
      validThrough: '2024-12-31',
      employmentType: 'FULL_TIME',
      hiringOrganization: {
        '@type': 'Organization',
        name: 'IGM Facility Service',
        sameAs: siteUrl,
        logo: `${siteUrl}/logo.png`,
      },
      jobLocation: {
        '@type': 'Place',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Industriestr. 37',
          addressLocality: 'Ratingen',
          postalCode: '40878',
          addressCountry: 'DE',
        },
      },
      baseSalary: {
        '@type': 'MonetaryAmount',
        currency: 'EUR',
        value: {
          '@type': 'QuantitativeValue',
          minValue: 2800,
          maxValue: 3500,
          unitText: 'MONTH',
        },
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'JobPosting',
      title: 'Objektleiter (m/w/d) Facility Management Ratingen',
      description: 'Objektleiter für die Koordination und Überwachung von Facility-Management-Projekten gesucht. Führungserfahrung und Organisationstalent erforderlich.',
      datePosted: '2024-01-15',
      validThrough: '2024-12-31',
      employmentType: 'FULL_TIME',
      hiringOrganization: {
        '@type': 'Organization',
        name: 'IGM Facility Service',
        sameAs: siteUrl,
        logo: `${siteUrl}/logo.png`,
      },
      jobLocation: {
        '@type': 'Place',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Industriestr. 37',
          addressLocality: 'Ratingen',
          postalCode: '40878',
          addressCountry: 'DE',
        },
      },
      baseSalary: {
        '@type': 'MonetaryAmount',
        currency: 'EUR',
        value: {
          '@type': 'QuantitativeValue',
          minValue: 3500,
          maxValue: 4500,
          unitText: 'MONTH',
        },
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'JobPosting',
      title: 'Auszubildende/r Gebäudereiniger (m/w/d) Ratingen',
      description: 'Starte deine Karriere als Gebäudereiniger! Wir bieten eine fundierte Ausbildung mit Übernahmechance und vielfältigen Entwicklungsmöglichkeiten.',
      datePosted: '2024-01-15',
      validThrough: '2024-12-31',
      employmentType: 'FULL_TIME',
      hiringOrganization: {
        '@type': 'Organization',
        name: 'IGM Facility Service',
        sameAs: siteUrl,
        logo: `${siteUrl}/logo.png`,
      },
      jobLocation: {
        '@type': 'Place',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Industriestr. 37',
          addressLocality: 'Ratingen',
          postalCode: '40878',
          addressCountry: 'DE',
        },
      },
      baseSalary: {
        '@type': 'MonetaryAmount',
        currency: 'EUR',
        value: {
          '@type': 'QuantitativeValue',
          minValue: 900,
          maxValue: 1200,
          unitText: 'MONTH',
        },
      },
    },
  ];

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <SEO
        title="Jobs & Karriere bei IGM Facility Service Ratingen | Jetzt bewerben"
        description="Karriere bei IGM Facility Service Ratingen: Offene Stellen als Reinigungskraft, Hausmeister, Objektleiter und Auszubildende. Faire Bezahlung, nettes Team. Jetzt bewerben!"
        keywords="Jobs Ratingen, Reinigungskraft Jobs, Hausmeister Jobs, Objektleiter, Ausbildung Gebäudereiniger, Karriere Facility Service"
        canonical="/jobs"
        jsonLd={jsonLd}
      />
      <Header />

      {/* Hero Section */}
      <section className="relative h-[400px] sm:h-[500px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://public.readdy.ai/ai/img_res/aca0006a7f128856dbedf91705d689a6.jpg" alt="Team" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30"></div>
        </div>
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6">Werde Teil unseres Teams</h1>
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl mb-8 text-gray-100">Gemeinsam gestalten wir die Zukunft der Gebäudedienstleistungen</p>
          <button onClick={() => document.getElementById('application-form')?.scrollIntoView({ behavior: 'smooth' })} className="bg-[#0D2B4E] hover:bg-[#0a2240] text-white px-8 py-4 rounded-lg text-base sm:text-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer w-full sm:w-auto min-h-[44px]">
            Jetzt bewerben
          </button>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Warum bei uns arbeiten?</h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600">Entdecke die Vorteile einer Karriere bei IGM Facility Service</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 group">
                <div className="w-16 h-16 bg-[#0D2B4E]/10 rounded-full flex items-center justify-center mb-6 group-hover:bg-[#0D2B4E] transition-colors duration-300">
                  <i className={`${benefit.icon} text-3xl text-[#0D2B4E] group-hover:text-white transition-colors duration-300`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Offene Stellen</h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600">Finde die perfekte Position für deine Karriere</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {openPositions.map((job, index) => (
              <div key={index} className="bg-white border-2 border-gray-200 rounded-xl p-8 hover:border-[#0D2B4E] hover:shadow-lg transition-all duration-300 group">
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 group-hover:text-[#0D2B4E] transition-colors duration-300">{job.title}</h3>
                  <div className="w-12 h-12 bg-[#0D2B4E]/10 rounded-lg flex items-center justify-center group-hover:bg-[#0D2B4E] transition-colors duration-300 flex-shrink-0">
                    <i className="ri-briefcase-line text-xl text-[#0D2B4E] group-hover:text-white transition-colors duration-300"></i>
                  </div>
                </div>
                <div className="flex flex-wrap gap-3 mb-4">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-[#0D2B4E]/10 text-[#0D2B4E]">
                    <i className="ri-time-line mr-1"></i>{job.type}
                  </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-700">
                    <i className="ri-map-pin-line mr-1"></i>{job.location}
                  </span>
                </div>
                <p className="text-gray-600 mb-6 leading-relaxed">{job.description}</p>
                <button onClick={() => { setFormData({ ...formData, position: job.title }); document.getElementById('application-form')?.scrollIntoView({ behavior: 'smooth' }); }} className="w-full bg-[#0D2B4E] hover:bg-[#0a2240] text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer min-h-[44px]">
                  Jetzt bewerben
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Culture Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Unsere Unternehmenskultur</h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">Bei IGM Facility Service steht der Mensch im Mittelpunkt. Wir fördern ein Arbeitsumfeld, das von Respekt, Zusammenarbeit und kontinuierlicher Weiterentwicklung geprägt ist.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {[
              { src: 'https://readdy.ai/api/search-image?query=professional%20cleaning%20team%20collaborating%20together%20in%20modern%20office%20space%2C%20diverse%20workers%20discussing%20work%20plans%2C%20friendly%20atmosphere%2C%20natural%20lighting%2C%20teamwork%20and%20cooperation%20concept&width=600&height=800&seq=culture-001&orientation=portrait', label: 'Teamarbeit & Zusammenhalt' },
              { src: 'https://readdy.ai/api/search-image?query=professional%20training%20session%20for%20facility%20management%20workers%2C%20instructor%20teaching%20modern%20cleaning%20techniques%2C%20educational%20environment%2C%20professional%20development%20and%20learning%20atmosphere&width=600&height=800&seq=culture-002&orientation=portrait', label: 'Schulung & Entwicklung' },
              { src: 'https://readdy.ai/api/search-image?query=happy%20facility%20management%20workers%20celebrating%20success%20together%2C%20positive%20work%20environment%2C%20modern%20workplace%2C%20employee%20satisfaction%20and%20achievement%20concept%2C%20professional%20photography&width=600&height=800&seq=culture-003&orientation=portrait', label: 'Erfolg & Wertschätzung' },
            ].map((item, i) => (
              <div key={i} className="relative h-64 sm:h-80 rounded-xl overflow-hidden shadow-lg group">
                <img src={item.src} alt={item.label} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-6">
                  <p className="text-white text-base sm:text-lg font-semibold">{item.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form Section */}
      <section id="application-form" className="py-20 px-4 bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Bewirb dich jetzt</h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600">Fülle das Formular aus und wir melden uns schnellstmöglich bei dir</p>
          </div>
          <div className="bg-gray-50 rounded-2xl p-6 sm:p-8 md:p-12 shadow-lg">
            <form id="job-application-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">Vollständiger Name <span className="text-red-500">*</span></label>
                <input type="text" id="name" name="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D2B4E] focus:border-transparent transition-all duration-300 text-sm min-h-[44px]" placeholder="Max Mustermann" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">E-Mail-Adresse <span className="text-red-500">*</span></label>
                  <input type="email" id="email" name="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D2B4E] focus:border-transparent transition-all duration-300 text-sm min-h-[44px]" placeholder="max@beispiel.de" />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">Telefonnummer <span className="text-red-500">*</span></label>
                  <input type="tel" id="phone" name="phone" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D2B4E] focus:border-transparent transition-all duration-300 text-sm min-h-[44px]" placeholder="+49 123 456789" />
                </div>
              </div>
              <div>
                <label htmlFor="position" className="block text-sm font-semibold text-gray-700 mb-2">Gewünschte Position <span className="text-red-500">*</span></label>
                <select id="position" name="position" value={formData.position} onChange={(e) => setFormData({ ...formData, position: e.target.value })} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D2B4E] focus:border-transparent transition-all duration-300 text-sm cursor-pointer min-h-[44px]">
                  <option value="">Bitte wählen...</option>
                  <option value="Reinigungskraft (m/w/d)">Reinigungskraft (m/w/d)</option>
                  <option value="Hausmeister (m/w/d)">Hausmeister (m/w/d)</option>
                  <option value="Schlüsseldienst-Techniker (m/w/d)">Schlüsseldienst-Techniker (m/w/d)</option>
                  <option value="Teamleiter Gebäudereinigung (m/w/d)">Teamleiter Gebäudereinigung (m/w/d)</option>
                  <option value="Andere Position">Andere Position</option>
                </select>
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">Nachricht / Motivation <span className="text-red-500">*</span></label>
                <textarea id="message" name="message" value={formData.message} onChange={(e) => { if (e.target.value.length <= 500) setFormData({ ...formData, message: e.target.value }); }} required rows={6} maxLength={500} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D2B4E] focus:border-transparent transition-all duration-300 resize-none text-sm" placeholder="Erzähle uns etwas über dich und warum du bei uns arbeiten möchtest..."></textarea>
                <p className="text-sm text-gray-500 mt-2">{formData.message.length}/500 Zeichen</p>
              </div>
              <div className="bg-[#0D2B4E]/5 border border-[#0D2B4E]/20 rounded-lg p-4">
                <p className="text-sm text-[#0D2B4E] flex items-start">
                  <i className="ri-information-line text-lg mr-2 mt-0.5 flex-shrink-0"></i>
                  <span><strong>Lebenslauf:</strong> Bitte sende deinen Lebenslauf nach Absenden des Formulars per E-Mail an <a href="mailto:jobs@igm-facility.de" className="underline font-semibold break-all">jobs@igm-facility.de</a></span>
                </p>
              </div>
              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center">
                  <i className="ri-checkbox-circle-line text-2xl text-green-600 mr-3 flex-shrink-0"></i>
                  <p className="text-green-800 font-medium text-sm sm:text-base">Vielen Dank für deine Bewerbung! Wir melden uns in Kürze bei dir.</p>
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
                  <i className="ri-error-warning-line text-2xl text-red-600 mr-3 flex-shrink-0"></i>
                  <p className="text-red-800 font-medium text-sm sm:text-base">Ein Fehler ist aufgetreten. Bitte versuche es erneut oder kontaktiere uns direkt.</p>
                </div>
              )}
              <button type="submit" className="w-full bg-[#0D2B4E] hover:bg-[#0a2240] text-white px-8 py-4 rounded-lg text-base sm:text-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer min-h-[44px]">
                Bewerbung absenden
              </button>
            </form>
          </div>
        </div>
      </section>

      <Footer />

      {showBackToTop && (
        <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="fixed bottom-4 right-4 w-10 h-10 sm:w-12 sm:h-12 bg-[#0D2B4E] hover:bg-[#0a2240] text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center z-50 cursor-pointer">
          <i className="ri-arrow-up-line text-lg sm:text-xl"></i>
        </button>
      )}
    </div>
  );
}